import 'src/styles/maintenance.css';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { fetcher } from 'src/api/fetcher';
import {
  cancelEvent,
  completeEvent,
  completeFulfillment,
  createDowntime,
  createEvent,
  createFulfillment,
  deleteEvent,
  deleteFulfillment,
  getMaintenanceDue,
  linkDowntime,
  skipFulfillment,
  startEvent,
  unlinkDowntime,
} from 'src/api/maintenance';
import DocumentViewerModal from 'src/components/document-viewer/DocumentViewerModal';
import Header from 'src/components/header.jsx';
import {
  FULFILLMENT_STATUS,
  FULFILLMENT_TERMINAL_STATUSES,
  MAINTENANCE_EVENT_STATUS,
  MAINTENANCE_SCOPE,
  MAINTENANCE_VIEW,
} from 'src/constants/maintenance';
import {
  maintenanceKeys,
  useDowntimesByUris,
  useEventFulfillmentsByEventUri,
  useLinkableDowntimes,
  useMaintenanceEvents,
  useMaintenanceRequirements,
} from 'src/hooks/services/useMaintenance';
import { useUsersByUri } from 'src/hooks/services/useUser';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { API_RESOURCES, PAGINATION_IGNORE_DEFAULT_LIMIT } from 'src/utils/constants';
import { buildEventNamesByUri, computeFulfillmentDiff, upsertByUri } from 'src/utils/maintenance';
import { getRouteURI, getUuid } from 'src/utils/url';

import CompleteEvent from './CompleteEvent';
import MaintenanceHome from './MaintenanceHome';
import MaintenanceStepper from './MaintenanceStepper';
import MaintenanceSuccess from './MaintenanceSuccess';
import RequirementSelect from './RequirementSelect';
import RunEvent from './RunEvent';
import ChangeDowntimeSheet from './sheets/ChangeDowntimeSheet';
import EventDetailSheet from './sheets/EventDetailSheet';
import VersionHistorySheet from './sheets/VersionHistorySheet';
import StartEvent from './StartEvent';

const VIEW_TITLES = {
  [MAINTENANCE_VIEW.HOME]: 'Maintenance',
  [MAINTENANCE_VIEW.SELECT]: 'Select requirements',
  [MAINTENANCE_VIEW.START]: 'Start maintenance',
  [MAINTENANCE_VIEW.RUN]: 'Maintenance in progress',
  [MAINTENANCE_VIEW.COMPLETE]: 'Complete event',
};

const loadFulfillments = async eventUri => {
  const response = await fetcher(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, {
    params: {
      'filter[maintenance_event]': eventUri,
      'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
    },
  });
  return response.resources || [];
};

const loadEvent = eventUri => fetcher(`${API_RESOURCES.MAINTENANCE_EVENT}/${getUuid(eventUri)}`);

const loadDowntime = downtimeUri =>
  downtimeUri
    ? fetcher(`${API_RESOURCES.DOWNTIME}/${getUuid(downtimeUri)}`)
    : Promise.resolve(null);

const MaintenanceFlow = ({
  anchor,
  instanceTargetUri,
  typeTargetUri,
  title,
  subtitle,
  icon,
  user,
  initialEventUuid,
}) => {
  const { addToast } = useToasts();
  const queryClient = useQueryClient();
  const { openActionPanel, closeActionPanel, closeAllActionPanels } = useActionPanelStore();

  const { data: reqData, isLoading: reqLoading } = useMaintenanceRequirements(
    instanceTargetUri,
    typeTargetUri
  );
  const { data: events = [], isLoading: eventsLoading } = useMaintenanceEvents(instanceTargetUri);
  const { data: linkableDowntimes = [] } = useLinkableDowntimes(
    anchor.downtimeWorkstationField,
    instanceTargetUri,
    anchor.canLinkDowntime
  );

  const eventUris = useMemo(
    () =>
      events
        .map(item => item.uri)
        .filter(Boolean)
        .sort(),
    [events]
  );
  const { data: fulfillmentsByEventUri = {} } = useEventFulfillmentsByEventUri(eventUris);

  // "Maintenance Event N" labels — rapidfab-parity numbering (oldest-first by
  // started_at || completed_at || downtime.start, uri tiebreaker).
  const linkedDowntimeUris = useMemo(
    () => events.map(event => event.downtime).filter(Boolean),
    [events]
  );
  const { data: downtimesByUri = {} } = useDowntimesByUris(linkedDowntimeUris);
  const actorUris = useMemo(
    () =>
      [
        ...new Set([
          ...events.map(item => item.completed_by || item.started_by).filter(Boolean),
          ...Object.values(downtimesByUri)
            .map(downtime => downtime?.assigned_user)
            .filter(Boolean),
        ]),
      ].sort(),
    [events, downtimesByUri]
  );
  const { data: usersByUri = {} } = useUsersByUri(actorUris, 'maintenance-event-actors');
  const eventNamesByUri = useMemo(
    () => buildEventNamesByUri(events, downtimesByUri),
    [events, downtimesByUri]
  );

  const requirements = useMemo(() => {
    const instance = (reqData?.instance || []).map(req => ({
      ...req,
      scope: MAINTENANCE_SCOPE.INSTANCE,
    }));
    const type = (reqData?.type || []).map(req => ({ ...req, scope: MAINTENANCE_SCOPE.TYPE }));
    const all = [...instance, ...type];
    const byUri = Object.fromEntries(all.map(req => [req.uri, req]));
    return { instance, type, all, byUri };
  }, [reqData]);

  const [view, setView] = useState(MAINTENANCE_VIEW.HOME);
  const [mode, setMode] = useState('new');
  const [event, setEvent] = useState(null);
  const [selectedUris, setSelectedUris] = useState([]);
  const [fulfillments, setFulfillments] = useState([]);
  const [linkedDowntime, setLinkedDowntime] = useState(null);
  const [runSummary, setRunSummary] = useState([]);
  const [isStarting, setIsStarting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [viewerFile, setViewerFile] = useState(null);
  const [maintenanceDueForEquipment, setMaintenanceDueForEquipment] = useState([]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const active = view !== MAINTENANCE_VIEW.HOME && !successState;
    document.body.classList.toggle('mnt-flow-active', active);
    return () => document.body.classList.remove('mnt-flow-active');
  }, [successState, view]);

  const seedEvent = nextEvent => {
    if (!nextEvent?.uri) return;
    queryClient.setQueryData(maintenanceKeys.event(getUuid(nextEvent.uri)), prev => ({
      ...(prev || {}),
      ...nextEvent,
    }));
    queryClient.setQueryData(maintenanceKeys.events(instanceTargetUri), prev => ({
      ...(prev || {}),
      resources: upsertByUri(prev?.resources || [], nextEvent),
    }));
  };

  const removeEvent = eventUri => {
    if (!eventUri) return;
    queryClient.setQueryData(maintenanceKeys.events(instanceTargetUri), prev => ({
      ...(prev || {}),
      resources: (prev?.resources || []).filter(item => item.uri !== eventUri),
    }));
  };

  const seedFulfillments = (eventUri, nextFulfillments) => {
    queryClient.setQueryData(maintenanceKeys.fulfillments(eventUri), {
      resources: nextFulfillments,
    });
    const batchedEventUris = eventUris.includes(eventUri)
      ? eventUris
      : [...eventUris, eventUri].filter(Boolean).sort();
    queryClient.setQueryData(maintenanceKeys.fulfillmentsForEvents(batchedEventUris), prev => {
      const previousResources = prev?.resources || [];
      return {
        ...(prev || {}),
        resources: [
          ...previousResources.filter(fulfillment => fulfillment.maintenance_event !== eventUri),
          ...nextFulfillments,
        ],
      };
    });
  };

  const loadMaintenanceDue = async () => {
    if (!instanceTargetUri) return;

    try {
      const data = await getMaintenanceDue(instanceTargetUri);
      setMaintenanceDueForEquipment(data);
    } catch {
      setMaintenanceDueForEquipment([]);
    }
  };

  useEffect(() => {
    loadMaintenanceDue();
  }, [instanceTargetUri]);

  // report_file.uri is the /document/<uuid>/ resource — resolve its `content`
  // (the actual file URL) before handing it to the viewer.
  const handleViewFile = async reportFile => {
    if (!reportFile) return;
    try {
      const doc = await fetcher(`${API_RESOURCES.DOCUMENT}/${getUuid(reportFile.uri)}`);
      setViewerFile({ uri: doc.content || null, filename: reportFile.filename || doc.name });
    } catch (_) {
      addToast('Could not open the attachment.', { appearance: 'error' });
    }
  };

  const resetFlow = () => {
    setMode('new');
    setEvent(null);
    setSelectedUris([]);
    setFulfillments([]);
    setLinkedDowntime(null);
    setRunSummary([]);
    setSuccessState(null);
    setView(MAINTENANCE_VIEW.HOME);
    // A completed/cancelled event may have closed its linked downtime; drop the
    // linkable-downtimes cache so the next event's Start screen refetches fresh
    // (otherwise a now-unavailable window still shows and 400s on link).
    queryClient.invalidateQueries({ queryKey: ['linkable-downtimes'] });
  };

  /* ── Entry: new / planned / resume ── */
  const startNewEvent = () => {
    setMode('new');
    setEvent(null);
    setLinkedDowntime(null);
    setFulfillments([]);
    // Fresh events start with nothing selected — the operator opts in.
    setSelectedUris(
      maintenanceDueForEquipment
        .filter(item => ['overdue', 'warning'].includes(item.status_indicator))
        .map(item => item.maintenance_requirement)
    );
    setView(MAINTENANCE_VIEW.SELECT);
  };

  const openPlanned = async plannedEvent => {
    closeActionPanel();
    setMode('planned');
    setEvent(plannedEvent);
    const [loadedFulfillments, downtime] = await Promise.all([
      loadFulfillments(plannedEvent.uri),
      loadDowntime(plannedEvent.downtime),
    ]);
    setFulfillments(loadedFulfillments);
    setLinkedDowntime(downtime);
    setSelectedUris(loadedFulfillments.map(fulfillment => fulfillment.maintenance_requirement));
    setView(MAINTENANCE_VIEW.SELECT);
  };

  const resumeEvent = async inProgressEvent => {
    closeActionPanel();
    setMode('resume');
    setEvent(inProgressEvent);
    const [loadedFulfillments, downtime] = await Promise.all([
      loadFulfillments(inProgressEvent.uri),
      loadDowntime(inProgressEvent.downtime),
    ]);
    setFulfillments(loadedFulfillments);
    setLinkedDowntime(downtime);
    setView(MAINTENANCE_VIEW.RUN);
  };

  // Deep link from rapidfab's wizard QR: ?event=<uuid> jumps straight into the
  // event (in-progress → Do the work; planned → Start screen) once the events
  // list has loaded. Fires once; unknown/terminal events stay on home.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!initialEventUuid || autoOpenedRef.current || eventsLoading) return;
    const target = events.find(item => getUuid(item.uri) === getUuid(initialEventUuid));
    if (!target) return;
    autoOpenedRef.current = true;
    if (target.status === MAINTENANCE_EVENT_STATUS.IN_PROGRESS) resumeEvent(target);
    else if (target.status === MAINTENANCE_EVENT_STATUS.PLANNED) openPlanned(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEventUuid, eventsLoading, events]);

  const toggleSelect = uri =>
    setSelectedUris(prev =>
      prev.includes(uri) ? prev.filter(item => item !== uri) : [...prev, uri]
    );

  // Select/unselect one scope group at a time (per-group "Select all").
  const toggleGroupAll = groupUris =>
    setSelectedUris(prev => {
      const allIn = groupUris.every(uri => prev.includes(uri));
      if (allIn) return prev.filter(uri => !groupUris.includes(uri));
      return [...new Set([...prev, ...groupUris])];
    });

  /* ── Start work (planned → in_progress) ── */
  const handleStartWork = async (downtimeDescriptor, planForLater = false) => {
    setIsStarting(true);
    let createdEventUri = null;
    let rollbackPlannedFulfillments = null;
    try {
      let currentEvent = event;
      // Build the fulfillment list from write responses (each GET-by-uuid
      // seeded) — never a LIST right after creating, which is eventually
      // consistent and would strand the Run screen.
      let finalFulfillments = fulfillments;

      if (mode === 'new') {
        currentEvent = await createEvent({ targetUri: instanceTargetUri }, addToast);
        createdEventUri = currentEvent.uri;
        finalFulfillments = await Promise.all(
          selectedUris.map(uri =>
            createFulfillment({ eventUri: currentEvent.uri, requirementUri: uri }, addToast)
          )
        );
      } else if (mode === 'planned') {
        // Reconcile the planned event's fulfillments with the current selection.
        // The add/remove writes and their rollback stay inline below: the rollback
        // must be registered before the mutations so a mid-reconcile failure can
        // undo partial progress (see the catch).
        const { toAdd, toRemove } = computeFulfillmentDiff(fulfillments, selectedUris);
        const addedFulfillments = [];
        const removedFulfillments = [];
        rollbackPlannedFulfillments = async () => {
          await Promise.all(
            addedFulfillments.map(fulfillment =>
              deleteFulfillment(getUuid(fulfillment.uri), addToast)
            )
          );
          await Promise.all(
            removedFulfillments.map(fulfillment =>
              createFulfillment(
                {
                  eventUri: currentEvent.uri,
                  requirementUri: fulfillment.maintenance_requirement,
                },
                addToast
              )
            )
          );
        };
        // Add first, then remove. If anything fails before start, rollback can
        // return the planned event to the original selection.
        // eslint-disable-next-line no-restricted-syntax
        for (const requirementUri of toAdd) {
          // eslint-disable-next-line no-await-in-loop
          const created = await createFulfillment(
            { eventUri: currentEvent.uri, requirementUri },
            addToast
          );
          addedFulfillments.push(created);
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const fulfillment of toRemove) {
          // eslint-disable-next-line no-await-in-loop
          await deleteFulfillment(getUuid(fulfillment.uri), addToast);
          removedFulfillments.push(fulfillment);
        }
        const removedUris = new Set(toRemove.map(fulfillment => fulfillment.uri));
        finalFulfillments = [
          ...fulfillments.filter(fulfillment => !removedUris.has(fulfillment.uri)),
          ...addedFulfillments,
        ];
      }

      // ── Plan for later: a future-starting downtime means scheduling, not working
      // now — link the downtime and leave the event PLANNED (skip the start). ──
      if (planForLater) {
        const planDescriptor =
          downtimeDescriptor?.create && user?.uri
            ? { create: { ...downtimeDescriptor.create, assignedUser: user.uri } }
            : downtimeDescriptor;
        let plannedDowntimeUri = planDescriptor?.existingUri || null;
        if (planDescriptor?.create) {
          const createdDowntime = await createDowntime(
            {
              ...planDescriptor.create,
              workstationField: anchor.downtimeWorkstationField,
              workstationUri: instanceTargetUri,
            },
            addToast
          );
          plannedDowntimeUri = createdDowntime.uri;
        }
        const linkedPlanned = await linkDowntime(
          getUuid(currentEvent.uri),
          plannedDowntimeUri,
          addToast
        );
        const planned = {
          ...currentEvent,
          ...linkedPlanned,
          status: MAINTENANCE_EVENT_STATUS.PLANNED,
          downtime: plannedDowntimeUri,
        };
        seedEvent(planned);
        seedFulfillments(planned.uri, finalFulfillments);
        setSuccessState({ type: 'planned', hadDowntime: true });
        return;
      }

      // Only send downtime on start when the event doesn't already have one.
      // A JIT-created downtime is assigned to the operator creating it, so the
      // planned/linked window carries a user (the "by …" chip).
      const descriptor =
        downtimeDescriptor?.create && user?.uri
          ? { create: { ...downtimeDescriptor.create, assignedUser: user.uri } }
          : downtimeDescriptor;
      const startedFromServer = await startEvent(
        getUuid(currentEvent.uri),
        linkedDowntime ? null : descriptor,
        addToast
      );
      const started = {
        ...currentEvent,
        ...startedFromServer,
        status: MAINTENANCE_EVENT_STATUS.IN_PROGRESS,
        ['started_at']:
          startedFromServer['started_at'] || currentEvent['started_at'] || new Date().toISOString(),
        downtime:
          startedFromServer.downtime ||
          linkedDowntime?.uri ||
          descriptor?.existingUri ||
          currentEvent.downtime ||
          null,
      };
      const downtime = await loadDowntime(started.downtime);
      seedEvent(started);
      seedFulfillments(started.uri, finalFulfillments);
      setEvent(started);
      setFulfillments(finalFulfillments);
      setLinkedDowntime(downtime);
      setMode('resume');
      setView(MAINTENANCE_VIEW.RUN);
      rollbackPlannedFulfillments = null;
    } catch (error) {
      // Roll back a half-created brand-new event so we don't strand it.
      if (mode === 'new' && createdEventUri) {
        try {
          await deleteEvent(getUuid(createdEventUri), addToast);
        } catch (error) {
          console.log(error);
        }
      }
      if (mode === 'planned' && rollbackPlannedFulfillments) {
        try {
          await rollbackPlannedFulfillments();
          const restoredFulfillments = await loadFulfillments(event.uri);
          setFulfillments(restoredFulfillments);
          setSelectedUris(
            restoredFulfillments.map(fulfillment => fulfillment.maintenance_requirement)
          );
        } catch (error) {
          console.log(error);
        }
      }
      addToast('Could not start the maintenance event.', { appearance: 'error' });
    } finally {
      setIsStarting(false);
    }
  };

  /* ── Complete / cancel ── */
  const handleComplete = async freeMachineNow => {
    setIsFinishing(true);
    try {
      const completedAt = new Date().toISOString();
      // Drive each requirement to its terminal state and write that terminal
      // state through locally. The API's immediate GET-by-uuid can read stale.
      const finalFulfillments = [];
      let anyFailed = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const item of runSummary) {
        if (FULFILLMENT_TERMINAL_STATUSES.includes(item.fulfillment.status)) {
          finalFulfillments.push(
            item.fulfillment.status === FULFILLMENT_STATUS.COMPLETE &&
              !item.fulfillment['completed_at']
              ? { ...item.fulfillment, ['completed_at']: completedAt }
              : item.fulfillment
          );
          continue;
        }
        const fulfillmentUuid = getUuid(item.fulfillment.uri);
        try {
          const nextStatus = item.skipped
            ? FULFILLMENT_STATUS.SKIPPED
            : FULFILLMENT_STATUS.COMPLETE;
          const updated = item.skipped
            ? await skipFulfillment(fulfillmentUuid, null, addToast)
            : await completeFulfillment(fulfillmentUuid, addToast);
          finalFulfillments.push({
            ...item.fulfillment,
            ...(updated || {}),
            status: nextStatus,
            ...(nextStatus === FULFILLMENT_STATUS.COMPLETE
              ? {
                  ['completed_at']:
                    updated?.['completed_at'] || item.fulfillment['completed_at'] || completedAt,
                }
              : {}),
          });
        } catch (_) {
          anyFailed = true;
          finalFulfillments.push(item.fulfillment);
        }
      }

      // Don't complete the event (and show success) if a requirement didn't save —
      // that would strand a non-terminal fulfillment under a completed event.
      if (anyFailed) {
        // Persist the transitions that DID succeed so a retry short-circuits them
        // instead of re-driving an already-complete fulfillment backwards
        // (complete -> in_progress, which the backend rejects and would wedge the
        // event so it can never complete). runSummary is the snapshot the Complete
        // screen retries against, so refresh it too - not just fulfillments.
        const finalByUri = new Map(finalFulfillments.map(item => [item.uri, item]));
        setRunSummary(prev =>
          prev.map(item => ({
            ...item,
            fulfillment: finalByUri.get(item.fulfillment.uri) || item.fulfillment,
          }))
        );
        seedFulfillments(event.uri, finalFulfillments);
        setFulfillments(finalFulfillments);
        addToast('Some checks could not be saved. Please retry before completing.', {
          appearance: 'error',
        });
        return;
      }

      const finalEventFromServer = await completeEvent(
        getUuid(event.uri),
        freeMachineNow,
        addToast
      );
      const finalEvent = {
        ...event,
        ...finalEventFromServer,
        status: MAINTENANCE_EVENT_STATUS.COMPLETE,
        ['completed_at']:
          finalEventFromServer['completed_at'] || event['completed_at'] || completedAt,
      };
      seedEvent(finalEvent);
      seedFulfillments(event.uri, finalFulfillments);
      setEvent(finalEvent);
      setFulfillments(finalFulfillments);
      await loadMaintenanceDue();

      setSuccessState({
        type: 'complete',
        hadDowntime: Boolean(linkedDowntime),
        freeNow: Boolean(freeMachineNow),
      });
    } catch (error) {
      addToast('Could not complete the event.', { appearance: 'error' });
    } finally {
      setIsFinishing(false);
    }
  };

  const handleCancel = async freeMachineNow => {
    setIsFinishing(true);
    const wasPlanned = event.status === MAINTENANCE_EVENT_STATUS.PLANNED;
    try {
      const finalEventFromServer = await cancelEvent(event, freeMachineNow, addToast);
      if (finalEventFromServer) {
        const finalEvent = {
          ...event,
          ...finalEventFromServer,
          status: MAINTENANCE_EVENT_STATUS.CANCELLED,
          ['completed_at']:
            finalEventFromServer['completed_at'] ||
            event['completed_at'] ||
            new Date().toISOString(),
        };
        seedEvent(finalEvent);
        setEvent(finalEvent);
      } else {
        // Planned event was deleted.
        removeEvent(event.uri);
      }
      setSuccessState({
        type: 'cancelled',
        wasPlanned,
        hadDowntime: Boolean(linkedDowntime),
        freeNow: Boolean(freeMachineNow),
      });
    } catch (error) {
      addToast('Could not cancel the event.', { appearance: 'error' });
    } finally {
      setIsFinishing(false);
    }
  };

  /* ── Downtime management (planned events) ── */
  const refreshEventDowntime = async () => {
    if (!event) return;
    const refreshed = await loadEvent(event.uri);
    const downtime = await loadDowntime(refreshed.downtime);
    setEvent(refreshed);
    setLinkedDowntime(downtime);
    seedEvent(refreshed);
    queryClient.invalidateQueries({ queryKey: ['linkable-downtimes'] });
  };

  const openChangeDowntime = () => {
    openActionPanel({
      title: 'Change downtime',
      content: (
        <ChangeDowntimeSheet
          anchor={anchor}
          event={event}
          instanceTargetUri={instanceTargetUri}
          linkedDowntime={linkedDowntime}
          assignedUserUri={user?.uri || null}
          onDone={async () => {
            closeActionPanel();
            await refreshEventDowntime();
          }}
        />
      ),
    });
  };

  const openUnlinkDowntime = async () => {
    try {
      await unlinkDowntime(getUuid(event.uri), addToast);
      addToast('Downtime unlinked. The window is kept on the schedule.', { appearance: 'success' });
      await refreshEventDowntime();
    } catch (error) {
      console.log(error);
    }
  };

  /* ── Detail sheets ── */
  const openVersionHistory = requirement =>
    openActionPanel({
      title: 'Version history',
      stack: true,
      content: <VersionHistorySheet requirement={requirement} />,
    });

  const handleCancelPlannedFromDetail = async detailEvent => {
    try {
      await cancelEvent(detailEvent, false, addToast);
      removeEvent(detailEvent.uri);
      closeActionPanel();
      addToast('Planned maintenance event removed.', { appearance: 'success' });
    } catch (_) {
      addToast('Could not remove the planned maintenance event.', { appearance: 'error' });
    }
  };

  // Stacked change-downtime sheet for an event opened from its detail sheet.
  const openChangeDowntimeFor = (detailEvent, detailDowntime) =>
    openActionPanel({
      title: 'Change downtime',
      stack: true,
      content: (
        <ChangeDowntimeSheet
          anchor={anchor}
          event={detailEvent}
          instanceTargetUri={instanceTargetUri}
          linkedDowntime={detailDowntime || null}
          assignedUserUri={user?.uri || null}
          onDone={async () => {
            closeAllActionPanels();
            const refreshed = await loadEvent(detailEvent.uri);
            seedEvent(refreshed);
          }}
        />
      ),
    });

  const openEventDetail = detailEvent => {
    openActionPanel({
      title: eventNamesByUri[detailEvent.uri] || 'Maintenance event',
      content: (
        <EventDetailSheet
          event={detailEvent}
          anchor={anchor}
          instanceTargetUri={instanceTargetUri}
          requirementByUri={requirements.byUri}
          onStart={() => openPlanned(detailEvent)}
          onResume={() => resumeEvent(detailEvent)}
          onViewFile={handleViewFile}
          onOpenHistory={openVersionHistory}
          onChangeDowntime={openChangeDowntimeFor}
          onCancelPlanned={handleCancelPlannedFromDetail}
        />
      ),
    });
  };

  /* ── Render ── */
  const headerTitle = successState ? 'Maintenance' : VIEW_TITLES[view] || 'Maintenance';

  let content;
  if (successState) {
    content = <MaintenanceSuccess successState={successState} onReset={resetFlow} />;
  } else if (view === MAINTENANCE_VIEW.HOME) {
    content = (
      <MaintenanceHome
        title={title}
        subtitle={subtitle}
        icon={icon}
        titleHref={
          anchor.resourceRoute && instanceTargetUri
            ? getRouteURI(anchor.resourceRoute, { uuid: getUuid(instanceTargetUri) })
            : null
        }
        titleIcon={anchor.instanceIcon}
        canLinkDowntime={anchor.canLinkDowntime}
        hasRequirements={requirements.all.length > 0}
        requirementsLoading={reqLoading}
        loading={eventsLoading}
        events={events}
        eventNamesByUri={eventNamesByUri}
        fulfillmentsByEventUri={fulfillmentsByEventUri}
        downtimesByUri={downtimesByUri}
        usersByUri={usersByUri}
        instanceTargetUri={instanceTargetUri}
        onStart={startNewEvent}
        onOpenPlanned={openEventDetail}
        onResume={resumeEvent}
        onDetails={openEventDetail}
        onOpenActivity={openEventDetail}
      />
    );
  } else if (view === MAINTENANCE_VIEW.SELECT) {
    content = (
      <RequirementSelect
        anchor={anchor}
        instanceReqs={requirements.instance}
        typeReqs={requirements.type}
        selected={selectedUris}
        maintenanceDueForEquipment={maintenanceDueForEquipment}
        onToggleGroup={toggleGroupAll}
        onToggle={toggleSelect}
        onCancel={resetFlow}
        onContinue={() => setView(MAINTENANCE_VIEW.START)}
      />
    );
  } else if (view === MAINTENANCE_VIEW.START) {
    content = (
      <StartEvent
        title={title}
        selectedCount={selectedUris.length}
        canLinkDowntime={anchor.canLinkDowntime}
        linkedDowntime={linkedDowntime}
        linkableDowntimes={linkableDowntimes}
        isStarting={isStarting}
        onBack={() => setView(MAINTENANCE_VIEW.SELECT)}
        onStartWork={handleStartWork}
        onChangeDowntime={openChangeDowntime}
        onUnlinkDowntime={openUnlinkDowntime}
      />
    );
  } else if (view === MAINTENANCE_VIEW.RUN) {
    content = (
      <RunEvent
        event={event}
        eventName={(event && eventNamesByUri[event.uri]) || 'Maintenance event'}
        fulfillments={fulfillments}
        requirementByUri={requirements.byUri}
        onViewFile={handleViewFile}
        onBack={resetFlow}
        onReviewComplete={summary => {
          setRunSummary(summary);
          setView(MAINTENANCE_VIEW.COMPLETE);
        }}
      />
    );
  } else if (view === MAINTENANCE_VIEW.COMPLETE) {
    content = (
      <CompleteEvent
        eventName={(event && eventNamesByUri[event.uri]) || 'Maintenance event'}
        summary={runSummary}
        linkedDowntime={linkedDowntime}
        isFinishing={isFinishing}
        onViewFile={handleViewFile}
        onBack={() => setView(MAINTENANCE_VIEW.RUN)}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <>
      <Header excludeFromHistory title={headerTitle} user={user} historyLabel='Maintenance' />
      <main
        role='main'
        className={classNames('mnt-root', {
          'mnt-root--flow': view !== MAINTENANCE_VIEW.HOME,
        })}
      >
        {!successState && <MaintenanceStepper activeView={view} />}
        {content}
      </main>
      <DocumentViewerModal
        isOpen={!!viewerFile}
        url={viewerFile?.uri || null}
        name={viewerFile?.filename || 'Document'}
        onClose={() => setViewerFile(null)}
      />
    </>
  );
};

MaintenanceFlow.propTypes = {
  anchor: PropTypes.object.isRequired,
  instanceTargetUri: PropTypes.string.isRequired,
  typeTargetUri: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.object.isRequired,
  user: PropTypes.object,
  initialEventUuid: PropTypes.string,
};

MaintenanceFlow.defaultProps = {
  initialEventUuid: null,
  typeTargetUri: null,
  subtitle: null,
  user: null,
};

export default MaintenanceFlow;
